import React from 'react';
import { Check, RefreshCw, Plus, CalendarClock, Eye, Loader2 } from 'lucide-react';
import T from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import openModal from 'App/services/Modal';

type Candidate = {
	rank: number;
	prospectingId: string;
	totalScore: number;
	name?: string;
	domain?: string;
	countryCode?: string;
	employees?: number;
	revenue?: number;
	city?: string;
	naceCode?: string;
	contacts?: Array<{ name: string; role: string; linkedin?: string; email?: string }>;
	newsSignals?: Array<{ type: string; title: string; summary?: string }>;
	inCRM?: boolean;
	crmClient?: { id: number; name: string; matchReason?: string };
	imported?: boolean;
	// Latest engagement plan already generated for this candidate (WEB-8017) —
	// present only for OUTREACH_AGENT customers.
	planId?: number;
	// Status of that latest plan (WEB-8041) — drives the row's Generating / View /
	// Retry affordance. A cancelled plan (WEB-8043) falls through to "View plan",
	// which opens the review's cancelled state with a regenerate action.
	planStatus?: 'generating' | 'ready' | 'failed' | 'cancelled';
};

const formatRevenue = (r: number | undefined): string => {
	if (!r) return '-';
	if (r >= 1_000_000_000) return `${(r / 1_000_000_000).toFixed(1)}B`;
	if (r >= 1_000_000) return `${(r / 1_000_000).toFixed(1)}M`;
	if (r >= 1_000) return `${(r / 1_000).toFixed(0)}K`;
	return String(r);
};

/**
 * A company's profile match, or the empty slot that says nobody asked.
 *
 * `score` is null for a run that skipped scoring - "Enrich contacts" does
 * (WEB-9697), because the screen that starts it renders no score. Those runs
 * still store `totalScore: 0`, so printing the number reads as "worst possible
 * match" when it means "never scored" (WEB-10894). The design gives that its
 * own mark: an empty dashed slot where the chip would be.
 */
const ScoreChip = ({ score }: { score: number | null }) => {
	if (score === null) {
		return (
			<span
				className="pa-score-none"
				title={T('prospectingAgent.notScored') || 'This run did not score its companies'}
			/>
		);
	}
	const s = Math.round(score * 100);
	const tone = s >= 85 ? 'success' : s >= 70 ? 'warn' : 'muted';
	return (
		<span
			className={`pa-score pa-score-${tone}`}
			title={
				T('prospectingAgent.profileMatchHint') ||
				"How closely this company matches your best customers' profile — industry, size and location. It doesn't rate how well your offering fits their needs."
			}
		>
			{s}
		</span>
	);
};

/**
 * Render a news-signal type as a human label. Uses the existing
 * `prospectingAgent.newsSignal.<type>` translation keys when available
 * (capital_raised → "Funding" / "Finansiering" etc.). Falls back to a
 * titlecased version of the raw type so a brand-new signal type still
 * renders something readable rather than a bare snake_case string.
 */
const formatSignalType = (type: string) => {
	const translated = T(`prospectingAgent.newsSignal.${type}`);
	if (translated && translated !== `prospectingAgent.newsSignal.${type}`) return translated;
	return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const CandidateRow = ({
	candidate,
	selected,
	syncing,
	onToggleSelect,
	onClick,
	onSync,
	onCreatePlan,
	onViewPlan,
	scored = true
}: {
	candidate: Candidate;
	selected: boolean;
	syncing: boolean;
	onToggleSelect: (pid: string) => void;
	onClick: () => void;
	onSync?: (pid: string, clientId: number, mode: 'sync' | 'new') => void;
	// Generate an engagement plan for this (already-in-CRM) company.
	onCreatePlan?: (pid: string, clientId: number) => void;
	// Reopen the candidate's existing plan (WEB-8017) — no new plan is created.
	onViewPlan?: (planId: number, pid: string, clientId: number) => void;
	/** False when the run skipped scoring, so the Match column shows no number. */
	scored?: boolean;
}) => {
	const c = candidate;
	const pid = c.prospectingId;
	const primaryContact = c.contacts?.[0];
	// An engagement plan needs someone to reach; with no decision-makers found yet
	// (e.g. the run hasn't finished enriching) it can only produce an empty plan, so
	// don't offer "Create plan" until there's a contact (WEB-8763).
	const hasContacts = (c.contacts?.length ?? 0) > 0;
	const metaParts = [
		c.domain,
		c.countryCode,
		c.city,
		c.employees ? `${c.employees} emp` : null,
		c.revenue ? `${formatRevenue(c.revenue)} SEK` : null
	].filter(Boolean);

	return (
		<tr className={selected ? 'on' : ''} onClick={onClick}>
			<td className="pa-col-check" onClick={e => e.stopPropagation()}>
				<input
					type="checkbox"
					checked={selected}
					disabled={!!c.imported || !!c.inCRM}
					onChange={() => onToggleSelect(pid)}
				/>
			</td>
			<td>
				<span className="pa-company-name">{c.name || pid}</span>
				{c.imported ? (
					<span className="pa-imported-badge">
						<Check style={{ width: 10, height: 10 }} />
						{'Imported'}
					</span>
				) : c.inCRM && c.crmClient ? (
					<span className="pa-crm-actions" onClick={e => e.stopPropagation()}>
						<a
							href={`/#/${AppService.getCustomerId()}/accounts/${c.crmClient.id}/`}
							className="pa-existing-badge"
							onClick={e => e.stopPropagation()}
						>
							{(T('prospectingAgent.inCRMAs') || 'In CRM as {name}').replace('{name}', c.crmClient.name)}
						</a>
						<span
							className={`pa-match-tag pa-match-tag--${
								c.crmClient.matchReason === 'orgNo'
									? 'org'
									: c.crmClient.matchReason === 'name'
									? 'name'
									: 'domain'
							}`}
						>
							{c.crmClient.matchReason === 'orgNo'
								? T('prospectingAgent.matchTagOrgNr') || 'org nr'
								: c.crmClient.matchReason === 'name'
								? T('prospectingAgent.crmMatchName') || 'name'
								: T('prospectingAgent.crmMatchDomain') || 'domain'}
						</span>
						{c.planStatus === 'generating' ? (
							<button
								type="button"
								className="pa-sync-btn"
								disabled
								title={T('prospectingAgent.planStatus.generating') || 'Generating'}
							>
								<Loader2 className="pa-spin" style={{ width: 11, height: 11 }} />
								{T('prospectingAgent.planStatus.generating') || 'Generating'}
							</button>
						) : c.planStatus === 'failed' && onCreatePlan ? (
							<button
								type="button"
								className="pa-sync-btn"
								onClick={e => {
									e.stopPropagation();
									onCreatePlan(pid, c.crmClient!.id);
								}}
								title={T('default.retry') || 'Retry'}
							>
								<RefreshCw style={{ width: 11, height: 11 }} />
								{T('default.retry') || 'Retry'}
							</button>
						) : c.planId && onViewPlan ? (
							<button
								type="button"
								className="pa-sync-btn"
								onClick={e => {
									e.stopPropagation();
									onViewPlan(c.planId!, pid, c.crmClient!.id);
								}}
								title={T('prospectingAgent.viewPlan') || 'View engagement plan'}
							>
								<Eye style={{ width: 11, height: 11 }} />
								{T('prospectingAgent.viewPlan') || 'View plan'}
							</button>
						) : onCreatePlan ? (
							<button
								type="button"
								className="pa-sync-btn"
								data-testid="pa-create-plan"
								disabled={!hasContacts}
								onClick={e => {
									e.stopPropagation();
									onCreatePlan(pid, c.crmClient!.id);
								}}
								title={
									hasContacts
										? T('prospectingAgent.createPlan') || 'Create engagement plan'
										: T('prospectingAgent.createPlanNoContacts') ||
										  'No contacts found for this company yet'
								}
							>
								<CalendarClock style={{ width: 11, height: 11 }} />
								{T('prospectingAgent.createPlan') || 'Create plan'}
							</button>
						) : null}
						{onSync ? (
							<>
								<button
									type="button"
									className="pa-sync-btn"
									disabled={syncing}
									onClick={e => {
										e.stopPropagation();
										const crmName = c.crmClient!.name;
										const companyName = c.name || pid;
										openModal('Alert', {
											title: T('prospectingAgent.syncToCRMTitle') || 'Sync to CRM',
											body: (
												T('prospectingAgent.syncToCRMBody') ||
												'Sync new contacts and prospecting data from "{company}" to "{crm}"?'
											)
												.replace('{company}', companyName)
												.replace('{crm}', crmName),
											confirmButtonText: T('prospectingAgent.syncShort') || 'Sync',
											onClose: (confirmed?: boolean) => {
												if (confirmed) onSync(pid, c.crmClient!.id, 'sync');
											}
										});
									}}
									title={
										T('prospectingAgent.syncRowTitle') ||
										'Sync new contacts and prospecting data to this client'
									}
								>
									<RefreshCw style={{ width: 11, height: 11 }} />
									{syncing
										? T('prospectingAgent.syncingShort') || 'Syncing...'
										: T('prospectingAgent.syncShort') || 'Sync'}
								</button>
								{c.crmClient.matchReason !== 'orgNo' ? (
									<button
										type="button"
										className="pa-sync-btn pa-sync-btn--new"
										disabled={syncing}
										onClick={e => {
											e.stopPropagation();
											const companyName = c.name || pid;
											openModal('Alert', {
												title: T('prospectingAgent.addAsNewTitle') || 'Add as new company',
												body: (
													T('prospectingAgent.addAsNewBody') ||
													'Add "{company}" as a new CRM client? This will create a separate record from "{existing}".'
												)
													.replace('{company}', companyName)
													.replace('{existing}', c.crmClient!.name),
												confirmButtonText: T('prospectingAgent.addAsNew') || 'Add as new',
												onClose: (confirmed?: boolean) => {
													if (confirmed) onSync(pid, c.crmClient!.id, 'new');
												}
											});
										}}
										title={
											T('prospectingAgent.addAsNewRowTitle') ||
											'Different company — add as a new client'
										}
									>
										<Plus style={{ width: 11, height: 11 }} />
										{T('prospectingAgent.addAsNew') || 'Add as new'}
									</button>
								) : null}
							</>
						) : null}
					</span>
				) : null}
				{metaParts.length > 0 ? <div className="pa-meta">{metaParts.join(' · ')}</div> : null}
			</td>
			<td>
				{c.newsSignals && c.newsSignals.length > 0 ? (
					<div className="pa-tags">
						{c.newsSignals.slice(0, 2).map((s, j) => (
							<span
								key={j}
								className={`pa-signal-tag${
									s.type === 'capital_raised' ||
									s.type === 'large_contract_won' ||
									s.type === 'expansion_into_new_market'
										? ' pa-signal-tag--high'
										: s.type === 'layoffs'
										? ' pa-signal-tag--warn'
										: ' pa-signal-tag--medium'
								}`}
								title={s.summary}
							>
								{formatSignalType(s.type)}
							</span>
						))}
					</div>
				) : c.naceCode ? (
					<div className="pa-tags">
						<span className="pa-signal-tag" title={T(`default.naceCode.${c.naceCode}`)}>
							{T(`default.naceCode.${c.naceCode}`)}
						</span>
					</div>
				) : (
					<span className="pa-meta">{'—'}</span>
				)}
			</td>
			<td>
				{primaryContact ? (
					<>
						<div className="pa-contact-name">{primaryContact.name}</div>
						<div className="pa-meta">{primaryContact.role}</div>
					</>
				) : (
					<span className="pa-meta" style={{ fontStyle: 'italic' }}>
						{'—'}
					</span>
				)}
			</td>
			<td className="pa-col-score">
				<ScoreChip score={scored ? c.totalScore : null} />
			</td>
		</tr>
	);
};

export default CandidateRow;
export { ScoreChip, formatRevenue };
export type { Candidate };
